import type { INPMetricWithAttribution } from 'web-vitals/attribution';

/**
 * Reports INP to Plausible, with the attribution that says what to fix.
 *
 * Search Console reports one number per url group, which is not enough to act on: INP is the sum
 * of the wait before the handler runs, the handler itself and the paint that follows, and each of
 * those has a different cure. The attribution build names the element that was interacted with and
 * splits the time between those three, so the event below says which interaction on which kind of
 * page is slow, and in which phase the time goes.
 *
 * Every property is bucketed or picked from a small set. Plausible counts unique property values,
 * and a raw millisecond figure or a full url would make a dimension nobody can read.
 *
 * Only interactions Google would hold against us are reported - `good` is under the same 200ms
 * that Search Console draws the line at. Plausible bills custom events beside pageviews, and the
 * pages that already respond quickly have nothing to tell us.
 */

export type PlausibleProps = Record<string, string>;

/** the page kinds as the urls spell them, which is the same in every market */
export function pageKind(pathname: string): string {
	if (pathname === '/') return 'home';
	if (pathname.includes('-p-')) return 'product';
	if (pathname.includes('-c-') || pathname.startsWith('/c/')) return 'category';
	return 'other';
}

/** wide enough buckets to stay readable, narrow around the 200ms threshold that matters */
export function durationBucket(ms: number): string {
	if (ms < 100) return '<100';
	if (ms < 200) return '100-200';
	if (ms < 300) return '200-300';
	if (ms < 500) return '300-500';
	if (ms < 1000) return '500-1000';
	return '1000+';
}

/** the phase holding most of the interaction, which is the one worth attacking first */
export function slowestPhase(attribution: INPMetricWithAttribution['attribution']): string {
	const phases: Array<[string, number]> = [
		['input delay', attribution.inputDelay],
		['processing', attribution.processingDuration],
		['presentation', attribution.presentationDelay],
	];
	return phases.reduce((slowest, phase) => (phase[1] > slowest[1] ? phase : slowest))[0];
}

/**
 * Dom ids that carry a number are records, not controls - `Select` writes the delivery address's
 * own id onto its options, so a slow click in the checkout would otherwise send a customer's
 * address id to Plausible, and give the dimension a value nobody will ever see twice.
 */
export function maskRecordIds(target: string): string {
	return target.replace(/#[\w-]*\d[\w-]*/g, '#id');
}

/**
 * A selector has to fit in a report, and what it is cut down to has to stay distinguishable: the
 * element interacted with sits at the right-hand end, `content-left > … > button.buy`, so the
 * ancestors are what goes.
 */
export function shortenTarget(target: string | undefined): string {
	if (!target) return 'unknown';
	const masked = maskRecordIds(target);
	return masked.length > 80 ? `…${masked.slice(-79)}` : masked;
}

/**
 * What an interaction belongs to, said in a word rather than in a css path.
 *
 * `interactionTarget` is the path to the element, and it is empty whenever the element has left the
 * dom before the metric is reported - which in a listing that re-renders is most of them. That is
 * why half of everything measured so far reads `unknown`, and why the half that does not is split
 * across paths differing only in which supercategory they sat under. web-vitals accepts a namer of
 * our own, called while the element is still there, so what arrives is the component.
 *
 * A component says what to open; a path says where it sat. `data-inp` on an element wins, so a
 * component can name itself when these class names stop being the right handle.
 */
const COMPONENT_FAMILIES: ReadonlyArray<readonly [string, string]> = [
	['load-more', 'load more'],
	['pagination', 'pagination'],
	['sorting', 'sort'],
	// the panel, its opener, its badges and its checkboxes are all filter-something
	['filter', 'filters'],
	['favorite-icon', 'favourite'],
	['product-box', 'product box'],
	['quantity', 'quantity'],
	['variant-dropdown', 'variant'],
	['image-gallery', 'image gallery'],
	['search-input', 'search'],
	['breadcrumbs', 'breadcrumbs'],
	['cart-item', 'cart item'],
	['to-checkout', 'checkout'],
];

/**
 * Names that are one word, where there is no part to end on. A third party spells its ids out in
 * full - `CybotCookiebotDialogBodyLevelButtonSliderWrapper` - so these match from the beginning.
 */
const COMPONENT_PREFIXES: ReadonlyArray<readonly [string, string]> = [['cybotcookiebot', 'cookiebot']];

/**
 * Whole names, because a prefix would reach too far. The listing's add to cart is `buy`, the product
 * page's is `buy-button` - and the footer's customer-type list is `buy-as`, which is not a buy
 * button at all and was reported as one until it was spelled out here.
 */
const COMPONENT_NAMES: ReadonlyArray<readonly [string, string]> = [
	['buy', 'buy'],
	['buy-area', 'buy'],
	['buy-button', 'buy'],
	['buy-button-area', 'buy'],
];

/**
 * A family shares a stem, and the stem can sit anywhere in the name: `variant-dropdown-header` and
 * `variant-dropdown-list` are both the variant, and so are `simple-search-input` and
 * `edit-order-product-box` their components.
 *
 * Whole hyphen-separated parts, both ends. `buy-as` is the footer's customer-type list and not a buy
 * button; `cart-items` is the container the items sit in, and the empty cart's link inside it is no
 * cart item. Either would be swept up by a stem that only had to start the name.
 */
function hasStem(token: string, stem: string): boolean {
	return `-${token}-`.includes(`-${stem}-`);
}

/** the tag, the id and every class, as the words a component can be recognised by */
function tokensOf(el: Element): string[] {
	return `${el.tagName} ${el.id} ${classNameOf(el)}`.toLowerCase().split(/\s+/).filter(Boolean);
}

function componentNameFor(el: Element): string | undefined {
	const tokens = tokensOf(el);

	const exact = COMPONENT_NAMES.find(([token]) => tokens.includes(token));
	if (exact) return exact[1];

	const family = COMPONENT_FAMILIES.find(([stem]) => tokens.some((token) => hasStem(token, stem)));
	if (family) return family[1];

	const prefixed = COMPONENT_PREFIXES.find(([stem]) => tokens.some((token) => token.startsWith(stem)));
	return prefixed?.[1];
}

/** a click lands on an icon inside a button inside a card: the component is a few steps up */
const ANCESTORS_SEARCHED = 15;

function classNameOf(el: Element): string {
	// svg elements carry an SVGAnimatedString rather than a string, and it is not what we match on
	return typeof el.className === 'string' ? el.className : '';
}

export function elementOf(node: Node | null): Element | null {
	if (!node) return null;
	return node.nodeType === 1 ? (node as Element) : node.parentElement;
}

export function componentOf(start: Element | null): string | undefined {
	const ancestry: Element[] = [];
	for (let el = start, step = 0; el && step < ANCESTORS_SEARCHED; step++, el = el.parentElement) {
		ancestry.push(el);
	}

	// a label wins wherever it sits, or it is no escape hatch: a nearer class would outrank it
	const labelled = ancestry.find((el) => el.getAttribute?.('data-inp'));
	if (labelled) return labelled.getAttribute('data-inp') ?? undefined;

	for (const el of ancestry) {
		// several of these are custom elements rather than classes - <product-box>, <cart-item>
		const name = componentNameFor(el);
		if (name) return name;
	}
	return undefined;
}

const CONTROLS = ['a', 'button', 'input', 'select', 'textarea', 'summary', 'label'];

/**
 * A label and the box it is for are one control, and a visitor ticking the box does not know or
 * care which of the two the pointer landed on - so a filter checkbox is one target either way
 * rather than `filters/label` beside `filters/input:checkbox`.
 */
function behindLabel(el: Element): Element {
	if (el.tagName.toLowerCase() !== 'label') return el;
	const control = (el as HTMLLabelElement).control ?? el.querySelector('input, select, textarea');
	return control ?? el;
}

/** a click lands on the label or the icon inside a button, and the button is what was pressed */
function nearestControl(start: Element | null): Element | null {
	let el: Element | null = start;
	for (let step = 0; el && step < ANCESTORS_SEARCHED; step++, el = el.parentElement) {
		if (CONTROLS.includes(el.tagName.toLowerCase()) || el.getAttribute?.('role') === 'button') {
			return behindLabel(el);
		}
	}
	return null;
}

/** what was touched, as a visitor would say it rather than as the dom spells it */
export function controlOf(start: Element | null): string {
	const el = nearestControl(start) ?? start;
	if (!el) return 'unknown';

	const tag = el.tagName.toLowerCase();
	if (tag === 'a') return 'link';
	if (tag === 'button') return 'button';
	if (tag === 'input') return `input:${(el as HTMLInputElement).type || 'text'}`;
	if (['img', 'svg', 'path', 'picture'].includes(tag)) return 'image';
	return tag;
}

/** enough of the path to tell two unnamed elements apart, and no more */
function shortPath(el: Element): string {
	const steps: string[] = [];
	let node: Element | null = el;
	for (let i = 0; node && i < 3; i++, node = node.parentElement) {
		const first = classNameOf(node).trim().split(/\s+/)[0];
		steps.unshift(first ? `${node.tagName.toLowerCase()}.${first}` : node.tagName.toLowerCase());
	}
	return steps.join('>');
}

/** passed to web-vitals as `generateTarget`, so it runs while the element is still in the dom */
export function describeTarget(node: Node | null): string | undefined {
	const el = elementOf(node);
	if (!el) return undefined;

	const component = componentOf(el);
	if (!component) return maskRecordIds(shortPath(el));

	/*
	 * Several of the storefront's controls are a div or an li with a click handler - the sorting
	 * chooser is both - and react attaches those at the root, so the dom holds nothing to recognise
	 * them by. Naming whichever tag the pointer happened to land on would split one control into
	 * `sort/div` and `sort/image` by where it was clicked, so a component with no real control
	 * under it is named by itself.
	 */
	const control = nearestControl(el);
	return maskRecordIds(control ? `${component}/${controlOf(control)}` : component);
}

/**
 * Whose code held the main thread.
 *
 * "Processing is 75% of the problem" is not something anyone can act on: the question is whether
 * that is our render or somebody else's tag. The long animation frame api names the script, and its
 * host is the part of that name worth counting - function names come from a minified bundle and
 * change with every build.
 */
const THIRD_PARTIES: ReadonlyArray<readonly [string, string]> = [
	['cookiebot', 'cookiebot'],
	['googletagmanager', 'google tag manager'],
	['google-analytics', 'google analytics'],
	['doubleclick', 'google ads'],
	['criteo', 'criteo'],
	['loop54', 'loop54'],
	['facebook', 'meta'],
	['plausible', 'plausible'],
];

export function scriptOrigin(sourceUrl: string | undefined, ownHost: string): string {
	if (!sourceUrl) return 'unattributed';

	let host: string;
	try {
		host = new URL(sourceUrl, `https://${ownHost}`).host.replace(/^www\./, '');
	} catch {
		return 'unattributed';
	}

	if (!host || host === ownHost.replace(/^www\./, '')) return 'our own code';

	const known = THIRD_PARTIES.find(([token]) => host.includes(token));
	return known ? known[1] : host;
}

/**
 * Where the time went, one level under the phase.
 *
 * The three phases say when; these say what. Script is ours or a third party's to fix, style and
 * layout is usually a selector or a measured read, and paint is the browser drawing what we asked
 * for - and no amount of memoising touches the last two.
 */
export function dominantWork(attribution: INPMetricWithAttribution['attribution']): string {
	const parts: Array<[string, number | undefined]> = [
		['script', attribution.totalScriptDuration],
		['style & layout', attribution.totalStyleAndLayoutDuration],
		['paint', attribution.totalPaintDuration],
		['unattributed', attribution.totalUnattributedDuration],
	];
	const measured = parts.filter((part): part is [string, number] => typeof part[1] === 'number');
	if (!measured.length) return 'unknown';

	return measured.reduce((most, part) => (part[1] > most[1] ? part : most))[0];
}

/** where the visitor was when an interaction began, kept per interaction rather than read later */
export type VisitedRoute = { time: number; pathname: string };

/**
 * The route an interaction started on. A click on a link changes the location inside the click
 * handler, long before the interaction is measured, so the location at reporting time is the page
 * the visitor landed on rather than the one whose button was slow.
 */
export function routeAt(routes: VisitedRoute[], time: number | undefined, fallback: string): string {
	if (time === undefined) return routes[routes.length - 1]?.pathname ?? fallback;
	const started = routes.filter((route) => route.time <= time);
	return (started.length ? started[started.length - 1] : routes[0])?.pathname ?? fallback;
}

export function inpProps(
	metric: INPMetricWithAttribution,
	pathname: string,
	ownHost = '',
	sample: string = FULL_STREAM_LABEL
): PlausibleProps {
	const { attribution } = metric;

	return {
		/*
		 * The route the worst interaction happened on, which is not where CrUX would file it: without
		 * soft navigations web-vitals measures a whole document, and CrUX attributes that to the url
		 * the document was loaded with. Naming the route under the slow interaction is what makes
		 * this answer "what should we fix"; comparing a slice of it to a Search Console url group is
		 * what it cannot do.
		 */
		page: pageKind(pathname),
		rating: metric.rating,
		inp: durationBucket(metric.value),
		phase: slowestPhase(attribution),
		interaction: attribution.interactionType ?? 'unknown',
		target: shortenTarget(attribution.interactionTarget),
		// an interaction during load is a different problem from one on a settled page
		load: attribution.loadState,
		// whose script held the thread, and what kind of work the time actually was
		script: scriptOrigin(attribution.longestScript?.entry?.sourceURL, ownHost),
		work: dominantWork(attribution),
		// which of the two streams this came from; only the sampled one can carry a percentile
		sample,
	};
}

/**
 * What makes a report the same one twice. web-vitals forces a final report when the page is
 * hidden, whether or not anything changed since the last one, so without this an ordinary visit
 * with one slow interaction would report - and Plausible would bill - that interaction twice.
 */
export function reportKey(metric: INPMetricWithAttribution): string {
	return `${metric.id}:${metric.value}`;
}

function sendToPlausible(props: PlausibleProps) {
	window.plausible('INP', { props });
}

/**
 * Starts reporting once, from the client entry. The library is loaded on its own rather than with
 * the app: it has nothing to do until an interaction happens, and measuring must not add to what
 * it measures. Without Plausible on the page there is nowhere to report, so nothing loads at all.
 *
 * Reported as the page's INP is established and each time a slower interaction replaces it, rather
 * than once the page is hidden, which is the default. The storefront is a single page app: by the
 * time a hidden page reports, the visitor may be two routes further on, and the event would name
 * the wrong kind of page. What this does not see is a slow interaction that never becomes the
 * page's worst - which is also what Search Console does not see, since INP is that worst one.
 */
/**
 * A one-in-twenty sample of every page, fast or slow, so the percentile can be read.
 *
 * Only interactions over 200ms were ever reported, which says what is slow but not how slow the
 * site is - and Search Console judges the 75th percentile of every interaction, which cannot be
 * found in a set that begins at 200. Hence a second, smaller stream: one page in twenty reports its
 * inp once, when the page is done, whatever that value came to. Those events are an unbiased sample
 * of pages, so `sample = 1 in 20` alone answers "where is the 75th percentile" with no weighting,
 * and it costs a twentieth of the visits rather than all of them.
 *
 * Keeping the two streams apart is the point. The `all` stream reports a slow interaction every
 * time a slower one replaces it, which is what makes it good for attribution and useless for a
 * percentile: a page can appear in it several times. A sampled page appears exactly once.
 *
 * The draw is made once per page rather than per report, so a page belongs wholly to one stream.
 */
export const FAST_SAMPLE_RATE = 0.05;
export const FAST_SAMPLE_LABEL = '1 in 20';
export const FULL_STREAM_LABEL = 'all';

export function reportInp(send: (props: PlausibleProps) => void = sendToPlausible, draw = Math.random): void {
	if (typeof window === 'undefined' || typeof window.plausible !== 'function') return;

	/*
	 * A sampled page contributes exactly one event, and it has to survive the three ways that can go
	 * wrong: the page ending before the web-vitals chunk has even loaded, which is likeliest on the
	 * slow connections this exists to measure; the visitor coming back to the tab, where a second
	 * ending would count one page twice; and a restore from the back-forward cache, which web-vitals
	 * treats as a fresh navigation with a metric of its own - so this has to start over with it, or
	 * the restored page either reports nothing or reports at once instead of at its end.
	 */
	let sampled = draw() < FAST_SAMPLE_RATE;
	let pendingReport: (() => void) | undefined;
	let pageEnded = false;
	let reported = false;
	const reportFinal = () => {
		if (!sampled) return;
		pageEnded = true;
		if (reported || !pendingReport) return;
		reported = true;
		const pending = pendingReport;
		pendingReport = undefined;
		pending();
	};

	// the event is fired at the document, so that is where it is listened for
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') {
			reportFinal();
			return;
		}
		/*
		 * The page is back, so it did not end after all. Leaving the flag set would make the first
		 * interaction after the visitor returns report on the spot - the page's inp as it stood a
		 * moment in, rather than what it finally came to - for anyone who backgrounded the page
		 * before touching anything.
		 */
		if (!reported) pageEnded = false;
	});
	// pagehide covers the browsers that unload without ever turning the page hidden
	window.addEventListener('pagehide', reportFinal);
	window.addEventListener('pageshow', (event) => {
		if (!event.persisted) return;
		// a restored page is another page view, and is drawn for the sample on its own merits
		sampled = draw() < FAST_SAMPLE_RATE;
		pendingReport = undefined;
		pageEnded = false;
		reported = false;
	});

	/*
	 * One entry per route the visitor interacted on, not per interaction: the reportedcandidate
	 * can be an older interaction once a visit is long enough, and its route has to still be here.
	 * Interactions on a route already recorded add nothing, since the entry before them answers
	 * for them, so a long visit costs one entry per page it passes through.
	 *
	 * The event's own timestamp rather than the time the listener ran: web-vitals reports the
	 * interaction's start time, and a route recorded a fraction later would look like the next one.
	 */
	const routes: VisitedRoute[] = [{ time: 0, pathname: window.location.pathname }];
	const noteRoute = (event: Event) => {
		const { pathname } = window.location;
		if (routes[routes.length - 1]?.pathname === pathname) return;
		routes.push({ time: event.timeStamp, pathname });
	};
	for (const event of ['pointerdown', 'keydown'] as const) {
		window.addEventListener(event, noteRoute, { capture: true, passive: true });
	}

	import('web-vitals/attribution')
		.then(({ onINP }) => {
			let lastReported = '';
			onINP(
				(metric) => {
					const report = (sample: string) => {
						const pathname = routeAt(routes, metric.attribution.interactionTime, window.location.pathname);
						send(inpProps(metric, pathname, window.location.host, sample));
					};

					/*
					 * A sampled page is held rather than reported, because the value is not final until
					 * the page is. Waiting for web-vitals' own last word does not work: its reporter
					 * remembers what it has handed over and stays quiet when the final value equals it,
					 * so a page whose inp settled before the visitor left would report nothing at all.
					 */
					if (sampled) {
						if (reported) return;
						pendingReport = () => report(FAST_SAMPLE_LABEL);
						// the page may already be over, with its ending spent before this arrived
						if (pageEnded) reportFinal();
						return;
					}

					if (metric.rating === 'good') return;
					if (reportKey(metric) === lastReported) return;
					lastReported = reportKey(metric);
					report(FULL_STREAM_LABEL);
				},
				{ reportAllChanges: true, generateTarget: describeTarget }
			);
		})
		.catch(() => {
			// measurement is not worth an error dialog, or a report of its own
		});
}
